import React, { useState, useCallback } from 'react'
import { 
    Button, 
    Dialog, 
    DialogTitle, 
    DialogContent, 
    DialogActions, 
    Typography, 
    CircularProgress,
    Alert,
    Box,
    LinearProgress,
    Divider
} from '@mui/material'
import { 
    Sync as SyncIcon, 
    CheckCircle as SuccessIcon, 
    Error as ErrorIcon,
    Warning as WarningIcon,
    AdminPanelSettings as AdminIcon
} from '@mui/icons-material'
import { 
    useNotify, 
    useRefresh 
} from 'react-admin'
import { isAdmin } from '@/lib/tools/permissions'
import fetchApi from '@/lib/tools/api'

interface GlobalSyncResponse {
    success: boolean
    message: string
    total_processed: number
    successful_syncs: number
    failed_syncs: number
    execution_time?: number
    errors?: string[]
}

export default function SyncAllSireneButton() {
    const [open, setOpen] = useState(false)
    const [loading, setLoading] = useState(false)
    const [result, setResult] = useState<GlobalSyncResponse | null>(null)
    
    const notify = useNotify()
    const refresh = useRefresh()

    const handleClickOpen = useCallback(() => {
        setOpen(true)
        setResult(null)
    }, [])

    const handleClose = useCallback(() => {
        setOpen(false)
        setResult(null)
        if (result && result.successful_syncs > 0) {
            refresh()
        }
    }, [refresh, result])

    const handleSyncAll = useCallback(async () => {
        setLoading(true)
        setResult(null)

        try {
            // Call the backend API to trigger global SIRENE sync
            const response = await fetchApi<GlobalSyncResponse>(
                `/producers/sync-all-sirene`,
                {},
                null,
                'POST'
            )

            setResult(response)

            if (response.successful_syncs > 0) {
                notify(
                    `${response.successful_syncs} producteurs synchronisés avec succès`,
                    { type: 'success' }
                )
            }

            if (response.failed_syncs > 0) {
                notify(
                    `${response.failed_syncs} synchronisations échouées`,
                    { type: 'warning' }
                )
            }

        } catch (error: any) {
            const errorMessage = error.message || 'Erreur lors de la synchronisation globale'
            notify(`Échec de la synchronisation: ${errorMessage}`, { type: 'error' })
            setResult({
                success: false,
                message: errorMessage,
                total_processed: 0,
                successful_syncs: 0,
                failed_syncs: 0
            })
        } finally {
            setLoading(false)
        }
    }, [notify])

    // Only show to admins
    if (!isAdmin()) {
        return null
    }

    const isCompleted = result !== null
    const executionTime = result?.execution_time ? Math.round(result.execution_time / 1000) : null

    return (
        <>
            <Button
                variant="contained"
                color="warning"
                startIcon={<AdminIcon />}
                onClick={handleClickOpen}
                disabled={loading}
                sx={{ 
                    ml: 1,
                    '&:hover': {
                        transform: 'scale(1.02)',
                        transition: 'transform 0.2s ease-in-out'
                    }
                }}
            >
                Sync Global SIRENE
            </Button>

            <Dialog
                open={open}
                onClose={loading ? undefined : handleClose}
                maxWidth="md"
                fullWidth
                aria-labelledby="sync-all-sirene-dialog-title"
                disableEscapeKeyDown={loading}
            >
                <DialogTitle id="sync-all-sirene-dialog-title">
                    <Box display="flex" alignItems="center" gap={1}>
                        <AdminIcon color="warning" />
                        Synchronisation Globale INSEE SIRENE
                    </Box>
                </DialogTitle>
                
                <DialogContent sx={{ minHeight: '300px' }}>
                    {isCompleted ? (
                        // Results view
                        <Box>
                            <Alert 
                                severity={result.success ? result.failed_syncs === 0 ? "success" : "warning" : "error"}
                                sx={{ mb: 3 }}
                            >
                                <Typography variant="body1" fontWeight="bold">
                                    {result.success ? 'Synchronisation globale terminée' : 'Erreur lors de la synchronisation'}
                                </Typography>
                                <Typography variant="body2">
                                    {result.message}
                                </Typography>
                                {result.success && (
                                    <Typography variant="body2" sx={{ mt: 1 }}>
                                        {result.successful_syncs} succès • {result.failed_syncs} échecs sur {result.total_processed} producteurs
                                        {executionTime && ` • Durée: ${executionTime}s`}
                                    </Typography>
                                )}
                            </Alert>

                            {result.success && (
                                <Box sx={{ mb: 3 }}>
                                    <Typography variant="h6" gutterBottom color="success.main">
                                        <SuccessIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                                        Statistiques de synchronisation
                                    </Typography>
                                    
                                    <Box sx={{ 
                                        display: 'grid', 
                                        gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', 
                                        gap: 2,
                                        mt: 2
                                    }}>
                                        <Alert severity="info" variant="outlined">
                                            <Typography variant="body2" fontWeight="bold">
                                                Total traité
                                            </Typography>
                                            <Typography variant="h4">
                                                {result.total_processed}
                                            </Typography>
                                        </Alert>
                                        
                                        <Alert severity="success" variant="outlined">
                                            <Typography variant="body2" fontWeight="bold">
                                                Synchronisations réussies
                                            </Typography>
                                            <Typography variant="h4" color="success.main">
                                                {result.successful_syncs}
                                            </Typography>
                                        </Alert>
                                        
                                        {result.failed_syncs > 0 && (
                                            <Alert severity="warning" variant="outlined">
                                                <Typography variant="body2" fontWeight="bold">
                                                    Échecs
                                                </Typography>
                                                <Typography variant="h4" color="warning.main">
                                                    {result.failed_syncs}
                                                </Typography>
                                            </Alert>
                                        )}
                                    </Box>

                                    {result.errors && result.errors.length > 0 && (
                                        <Box sx={{ mt: 3 }}>
                                            <Typography variant="body2" fontWeight="bold" gutterBottom>
                                                Erreurs détectées :
                                            </Typography>
                                            <Box sx={{ 
                                                maxHeight: '150px', 
                                                overflow: 'auto',
                                                border: 1,
                                                borderColor: 'warning.main',
                                                borderRadius: 1,
                                                p: 1,
                                                bgcolor: 'warning.light',
                                                opacity: 0.1
                                            }}>
                                                {result.errors.map((error, index) => (
                                                    <Typography key={index} variant="body2" sx={{ mb: 0.5 }}>
                                                        • {error}
                                                    </Typography>
                                                ))}
                                            </Box>
                                        </Box>
                                    )}
                                </Box>
                            )}
                        </Box>
                    ) : loading ? (
                        // Loading view
                        <Box>
                            <Alert severity="info" sx={{ mb: 3 }}>
                                <Box display="flex" alignItems="center" gap={2}>
                                    <CircularProgress size={20} />
                                    <Typography variant="body1">
                                        Synchronisation globale en cours...
                                    </Typography>
                                </Box>
                            </Alert>

                            <Box sx={{ mb: 2 }}>
                                <LinearProgress 
                                    variant="indeterminate" 
                                    sx={{ 
                                        height: 8, 
                                        borderRadius: 4,
                                        mb: 1
                                    }}
                                />
                                <Typography variant="body2" color="text.secondary" align="center">
                                    Traitement de tous les producteurs de la base de données...
                                </Typography>
                                <Typography variant="caption" color="text.secondary" align="center" display="block" sx={{ mt: 1 }}>
                                    Cette opération peut prendre plusieurs minutes selon le nombre de producteurs.
                                </Typography>
                            </Box>

                            <Alert severity="warning" sx={{ mt: 2 }}>
                                <Typography variant="body2">
                                    <strong>Attention :</strong> Ne fermez pas cette fenêtre pendant le traitement.
                                    La synchronisation se poursuit en arrière-plan.
                                </Typography>
                            </Alert>
                        </Box>
                    ) : (
                        // Confirmation view
                        <Box>
                            <Alert severity="error" sx={{ mb: 3 }}>
                                <Typography variant="body1" fontWeight="bold">
                                    <WarningIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                                    ATTENTION - Action Administrateur
                                </Typography>
                                <Typography variant="body2">
                                    Cette action va synchroniser TOUS les producteurs de la base de données 
                                    avec les données INSEE SIRENE. Cette opération est réservée aux administrateurs.
                                </Typography>
                            </Alert>

                            <Typography variant="h6" gutterBottom>
                                Synchronisation Globale SIRENE
                            </Typography>
                            
                            <Typography variant="body2" sx={{ mb: 2 }}>
                                <strong>Cette action va :</strong>
                            </Typography>
                            <Box component="ul" sx={{ pl: 2, m: 0, mb: 3 }}>
                                <Typography component="li" variant="body2">
                                    Traiter TOUS les producteurs enregistrés
                                </Typography>
                                <Typography component="li" variant="body2">
                                    Mettre à jour les statuts administratifs (Actif/Cessé)
                                </Typography>
                                <Typography component="li" variant="body2">
                                    Actualiser les dates de cessation d'activité
                                </Typography>
                                <Typography component="li" variant="body2">
                                    Synchroniser toutes les données SIRENE disponibles
                                </Typography>
                            </Box>

                            <Divider sx={{ my: 2 }} />

                            <Alert severity="warning" sx={{ mb: 2 }}>
                                <Typography variant="body2">
                                    <strong>Estimation :</strong> Cette opération peut prendre de plusieurs minutes 
                                    à plus d'une heure selon le nombre de producteurs dans la base de données.
                                </Typography>
                            </Alert>

                            <Alert severity="info">
                                <Typography variant="body2">
                                    <strong>Recommandation :</strong> Effectuez cette synchronisation pendant 
                                    les heures de faible affluence pour éviter d'impacter les performances 
                                    de l'application.
                                </Typography>
                            </Alert>
                        </Box>
                    )}
                </DialogContent>
                
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    {isCompleted ? (
                        <Button onClick={handleClose} color="primary" variant="contained">
                            Fermer
                        </Button>
                    ) : (
                        <>
                            <Button 
                                onClick={handleClose} 
                                disabled={loading}
                                color="inherit"
                            >
                                Annuler
                            </Button>
                            <Button
                                onClick={handleSyncAll}
                                disabled={loading}
                                variant="contained"
                                color="warning"
                                startIcon={loading ? <CircularProgress size={20} /> : <SyncIcon />}
                            >
                                {loading ? 'Synchronisation globale...' : 'Confirmer la synchronisation globale'}
                            </Button>
                        </>
                    )}
                </DialogActions>
            </Dialog>
        </>
    )
}